@import '@styles/fonts.scss';
@import '@styles/mixins.scss';
@import '@styles/colors.scss';
@import '@styles/animations.scss';

/* -------------------------------------------------------------------------- */
/*  Page shell                                                                 */
/* -------------------------------------------------------------------------- */

.page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--bg);
}

/* -------------------------------------------------------------------------- */
/*  Detail hero — sticky left + 3 labeled rows right (handoff §0.5)          */
/* -------------------------------------------------------------------------- */

.detailHero {
    padding: 56px 64px 32px;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;

    @media (max-width: 900px) {
        padding: 40px 32px 24px;
    }

    @media (max-width: 600px) {
        padding: 32px 20px 20px;
    }
}

.detailHero__grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 32px;
    align-items: start;

    @media (max-width: 899px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
}

.detailHero__left {
    grid-column: span 5;
    position: sticky;
    top: 80px;
    align-self: start;

    /* styles/partials/typography.scss applies the legacy `v-rhythm(0)` mixin to every `p`, and it
       carries `padding-top: .5em` — dead space above the text that scales with each paragraph's own
       size: 18px on a 36px line, 8.5px on the tagline, 5.5px on a note. It puts ~30px of
       invisible height in this column and, worse, means an element's box stops describing its text,
       so margins tuned against it read wrong (this is what made the price row look like it needed a
       negative margin once the stat pills were retired). Ovid's own reset zeroes the same property
       — this column now does too. The h1 is in for the same reason: `v-rhythm(4)` gives it
       `padding-top: .255em`, another 14px at 56px. The global rule is still live for the rest of
       narcis; retiring it there is a wider decision than this file. */
    p,
    h1 {
        padding-top: 0;
    }

    @media (max-width: 899px) {
        grid-column: 1 / -1;
        position: static;
        order: 2;
        /* The phone's first screen owes the reader the picture and the verb; the tagline opens the
           body below. The left column becomes a flex column here so the tagline can sink under
           the aside (order: 1, set on .detailHero__tagline) while the source order stays prose. */
        display: flex;
        flex-direction: column;
        // Root-cause fix (2026-07-24, operator phone report): a CSS Grid item's automatic
        // min-width is content-based ("auto"), not 0. Below 640px the thumb strip switches to
        // horizontal scroll-snap with 9 non-shrinking 44px thumbs (PackHeroStage.module.scss
        // .thumb `flex: 0 0 44px`) — 9×44 + 8×6px gap = 444px of intrinsic content. Without this
        // override, that content bubbles through .thumbStrip's own overflow-x:auto (which only
        // rescues .thumbStrip itself) and forces this single 1fr grid track to 444px — 74px past
        // a 390px viewport — dragging the whole document (including the sticky header) into real
        // horizontal overflow. Confirmed live: forcing min-width:0 here collapsed the grid track
        // from 443.9px back to 350px (the actual available width) and .thumbStrip's own
        // overflow-x:auto took over as intended, dropping document scrollWidth to exactly the
        // 390px viewport. Chrome's own window-resize automation floors at ~555px (never reaches
        // the <640px breakpoint that triggers this), which is why no prior verification pass —
        // including the canvas 06 revision pass that introduced the 44px thumb floor — caught it.
        min-width: 0;
    }
}

.detailHero__eyebrow {
    font-family: var(--font-mono);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--yolk);
    margin: 0 0 14px;
}

.detailHero__heading {
    font-family: var(--font-display);
    font-style: italic;
    font-weight: 400;
    font-size: 56px;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--yolk);
    margin: 0 0 14px;

    @media (max-width: 900px) {
        font-size: 40px;
    }

    @media (max-width: 600px) {
        font-size: 32px;
    }
}

.detailHero__tagline {
    @media (max-width: 899px) {
        order: 1;
    }
    font-family: var(--font-body);
    font-size: 17px;
    line-height: 1.5;
    color: var(--text-secondary, var(--text-muted));
    margin: 0 0 24px;

    strong {
        color: var(--text);
        font-weight: 600;
    }
}

// The canon's pack line (`proposition.ts` PACK_LINE) — where the price row stood, shown while
// payment is asked.
.detailHero__packLine {
    font-size: 15px;
    color: var(--text-muted);
    line-height: 1.5;
    margin: 0 0 24px;
}

// Generic note line — copy spec §3 B/C/D "note" strings (Sign-in comes at training / Remove
// from queue / Saved to My Packs).
.detailHero__note {
    font-size: 13px;
    color: var(--text-dim);
    line-height: 1.6;
    margin: 14px 0 0;

    em {
        color: var(--text-muted);
        font-style: normal;
    }
}

// Inline text-styled buttons inside a note paragraph (C-state "Remove from queue") — visually a
// link, semantically a button (no navigation, a state mutation).
.inlineLinkButton {
    display: inline;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover { color: var(--text-muted); }
    &:focus-visible { outline: 2px solid var(--yolk); outline-offset: 2px; }
}

// Queue chip — copy spec §3 state C, "● QUEUED · RENDERS WHEN YOUR MODEL IS READY". Canvas 02-C
// `.qchip` / canvas 06 §09 `.bdg.q`.
.queueChip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--yolk-dim);
    border: 1px solid var(--yolk-med);
    color: var(--yolk);
    border-radius: var(--radius-pill, 9999px);
    padding: 5px 13px;
    font-family: var(--font-mono);
    font-size: 10.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin: 0 0 16px;
}

.queueChipDot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--yolk);
    animation: trainCardDotPulse 1.4s ease-in-out infinite;
}

// Ghost queue/browse button — B's "+ Queue this pack for your model" / C's "Browse more styles
// while it trains →". Canvas 02 `.pk-ghost` (btn-ghost) + canvas 06 §05 (queued state).
.queueGhost {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    margin-top: 10px;
    font-family: var(--font-body);
    font-size: 13.5px;
    font-weight: 600;
    color: var(--text-muted);
    background: transparent;
    border: 1px solid var(--border-med);
    border-radius: var(--radius-md, 10px);
    padding: 11px 20px;
    cursor: pointer;
    min-height: 44px;
    transition: border-color 150ms ease, color 150ms ease, background 150ms ease;

    &:hover { border-color: var(--yolk-med); color: var(--yolk); }
    &:focus-visible { outline: 2px solid var(--yolk); outline-offset: 2px; }
    &:disabled { cursor: default; }
}

.queueGhostOn {
    background: var(--yolk-dim);
    border-color: var(--yolk-med);
    color: var(--yolk);
}

// The wall write's own failure line (narcis wall plan step 6 § 3) — placement only, the Notice
// component's own kind sets the colour (`@core/ui/Notice`, four `--notice-*` tokens narcis's
// `styles/partials/colors.scss` already defines).
.wallPackNote {
    display: block;
    margin-top: 10px;
    font-family: var(--font-mono);
    font-size: 12px;
}

.detailHero__cta {
    display: block;
    width: 100%;
    box-sizing: border-box;
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: 500;
    color: var(--ink);
    background: var(--yolk);
    padding: 14px 24px;
    border-radius: var(--radius-md, 10px);
    text-decoration: none;
    border: none;
    cursor: pointer;
    text-align: center;
    transition: background 200ms ease;

    &:hover {
        background: var(--yolk-hover);
        color: var(--ink);
    }

    &:focus-visible {
        outline: 2px solid var(--yolk);
        outline-offset: 3px;
    }
}

.detailHero__ctaInner {
    display: block;
}

.detailHero__ctaDisabled {
    opacity: 0.6;
    cursor: default;
    pointer-events: none;
}

// Hidden on mobile (<1024): the stage's own CTA is the single primary on mobile.
// On desktop (≥1024) this is the single primary CTA (stage CTA is hidden there via PackHeroStage.module.scss).
.detailHero__ctaMobileHidden {
    @media (max-width: 1023px) {
        display: none;
    }
}

// Mobile-only eyebrow + title block: visible only below 1024px, sits above the grid (order:0).
// Duplicates the semantic content of the in-aside eyebrow/heading; the in-aside copies are
// hidden on mobile via detailHero__desktopOnly. The mobileHeader div is aria-hidden because
// the real headings below remain in the DOM for screen readers on all breakpoints.
.detailHero__mobileHeader {
    display: none;

    @media (max-width: 1023px) {
        display: block;
        margin-bottom: 16px;
    }
}

// Hide on mobile (<1024): the mobileHeader carries the duplicate eyebrow+title for mobile.
.detailHero__desktopOnly {
    @media (max-width: 1023px) {
        display: none;
    }
}

// Inverse of the above — mobile-only truncated copy variants (copy spec §3b: "mobile may only
// truncate, never rephrase"). Hidden ≥1024, shown <1024.
.detailHero__mobileOnly {
    display: none;

    @media (max-width: 1023px) {
        display: inline;
    }
}

.detailHero__right {
    grid-column: span 7;
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;

    @media (max-width: 899px) {
        grid-column: 1 / -1;
        // On mobile: stage appears after title/lede and before chips/price/CTA.
        // Left col eyebrow+title+lede rendered first (order:0 default) while we
        // split left col rendering by making left col order:2 and stage order:1.
        order: 1;
        align-items: stretch;
        // Same fix as .detailHero__left above, same root cause — the stage/thumbStrip subtree
        // lives in THIS column, so this is the side that actually carries the offending content.
        min-width: 0;
    }
}

.detailRow {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.detailRow__label {
    font-family: var(--font-mono);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--color-plasma, var(--yolk));
}

.detailRow__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.detailRow__cell {
    aspect-ratio: 2 / 3;
    overflow: hidden;
    border-radius: var(--radius-sm, 6px);
    background: var(--color-bg-2, var(--surface));
}

.detailRow__cell img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
}

/* -------------------------------------------------------------------------- */
/*  Gallery proof — below the hero (moved from hero right-column, Phase 2)   */
/* -------------------------------------------------------------------------- */

.galleryProof {
    padding: 32px 64px 0;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
    overflow: hidden;

    @media (max-width: 900px) {
        padding: 24px 32px 0;
    }

    @media (max-width: 600px) {
        padding: 20px 20px 0;
    }
}

.galleryProofInner {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

/* -------------------------------------------------------------------------- */
/*  Returning-buyer banner — above workbench, dismissible                     */
/* -------------------------------------------------------------------------- */

.returningBuyerBanner {
    @include font-body;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: min(880px, 100% - 64px);
    margin: 20px auto 0;
    padding: 10px 16px;
    background: var(--yolk-dim);
    border: 1px solid var(--yolk-med);
    border-radius: 10px;
    font-size: 13px;
    color: var(--text);
}

.returningBuyerLink {
    color: var(--yolk);
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.returningBuyerDismiss {
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    color: var(--text-dim);
    padding: 2px 4px;
    line-height: 1;
    border-radius: 4px;
    transition: color 0.15s ease;

    &:hover {
        color: var(--text);
    }

    &:focus-visible {
        outline: 3px solid var(--yolk);
        outline-offset: 2px;
    }
}

/* -------------------------------------------------------------------------- */
/*  Below-workbench sections — How it works                                   */
/* -------------------------------------------------------------------------- */

.belowWorkbench {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 2rem;
}

.howSection {
    border-top: 1px solid var(--border);
    padding: 4rem 0;
}

.howHeading {
    @include font-display;
    font-size: 36px;
    font-weight: 400;
    color: var(--text);
    letter-spacing: -0.72px;
    margin: 0 0 0.5rem;
    text-align: center;
}

.howSubhead {
    @include font-body;
    font-size: 14px;
    color: var(--text-muted);
    margin: 0 0 2.5rem;
    text-align: center;
}

.howSteps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.howStep {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: var(--surface);
    border-radius: var(--radius-lg);
    padding: 24px;
}

.howStepNumber {
    @include font-display;
    font-size: 14px;
    font-weight: 400;
    color: var(--text-muted);
    line-height: 1;
    margin: 0;
}

.howStepTitle {
    @include font-body;
    font-size: 1rem;
    font-weight: 600;
    color: var(--text);
    margin: 0;
}

.howStepBody {
    @include font-body;
    font-size: 0.9rem;
    color: var(--text-muted);
    line-height: 1.6;
    margin: 0;
}

/* -------------------------------------------------------------------------- */
/*  SEO content backbone — below workbench (style guide, prep tips, FAQ,      */
/*  use cases, about narcis)                                                  */
/* -------------------------------------------------------------------------- */

.seoContent {
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    padding: 0 2rem;
    box-sizing: border-box;
}

.seoSection {
    border-top: 1px solid var(--border);
    padding: 3rem 0;
}

.seoHeading {
    @include font-display;
    font-size: 28px;
    font-weight: 400;
    color: var(--text);
    letter-spacing: -0.02em;
    margin: 0 0 1.5rem;

    @media (max-width: 600px) {
        font-size: 22px;
    }
}

.seoParagraph {
    @include font-body;
    font-size: 15px;
    line-height: 1.7;
    color: var(--text-muted);
    margin: 0 0 1rem;

    &:last-child {
        margin-bottom: 0;
    }
}

/* Prep tips — ordered list */
.seoPrepList {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    counter-reset: prep-counter;
}

.seoPrepItem {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.5rem 1rem;
    counter-increment: prep-counter;
    align-items: baseline;

    &::before {
        content: counter(prep-counter) '.';
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--text-dim);
        align-self: start;
        padding-top: 3px;
    }
}

.seoPrepName {
    @include font-body;
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    grid-column: 2;
    grid-row: 1;
}

.seoPrepText {
    @include font-body;
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-muted);
    grid-column: 2;
    grid-row: 2;
}

/* FAQ — definition list */
.seoFaqList {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.seoFaqItem {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.seoFaqQ {
    @include font-body;
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin: 0;
}

.seoFaqA {
    @include font-body;
    font-size: 14px;
    line-height: 1.7;
    color: var(--text-muted);
    margin: 0;
}

/* Use cases — unordered list */
.seoUseCasesList {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.seoUseCasesItem {
    @include font-body;
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-muted);
    padding-left: 1.25rem;
    position: relative;

    &::before {
        content: '—';
        position: absolute;
        left: 0;
        color: var(--text-dim);
    }
}

/* -------------------------------------------------------------------------- */
/*  Legal note                                                                 */
/* -------------------------------------------------------------------------- */

.legalNote {
    @include font-body;
    font-size: 0.75rem;
    color: var(--text-dim);
    text-align: center;
    padding: 2rem 2rem;
    max-width: 520px;
    margin: 0 auto;

    a {
        color: var(--text-muted);
        text-underline-offset: 2px;
    }
}

/* -------------------------------------------------------------------------- */
/*  Responsive                                                                 */
/* -------------------------------------------------------------------------- */

/* 768–1023px: 2-col workbench, narrower padding */
@media (max-width: 1023px) and (min-width: 768px) {

    .returningBuyerBanner {
        width: calc(100% - 48px);
    }

    .howSteps {
        grid-template-columns: repeat(3, 1fr);
    }
}

/* ≤767px: workbench stack */
@media (max-width: 767px) {

    .returningBuyerBanner {
        width: calc(100% - 24px);
        margin-left: 12px;
        margin-right: 12px;
    }

    .howSteps {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }

    .belowWorkbench {
        padding-left: 1rem;
        padding-right: 1rem;
    }
}
